import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { APP } from "../constants/testIds";
import { Flame, Shield, Languages, UserRound } from "lucide-react";
import { AdminSummary } from "../components/AdminSummary";
import { StorageSavings } from "../components/StorageSavings";
import { MemberProgressPanel } from "../components/MemberProgressPanel";

const SCOPE_LINKS = {
  member: [{ to: "/my-journey", key: "nav.journey" }, { to: "/programs", key: "nav.programs" }],
  coach: [{ to: "/my-journey", key: "journey.myMembers" }, { to: "/education", key: "nav.education" }],
  admin: [{ to: "/programs", key: "nav.programs" }, { to: "/admin/payments", key: "nav.payments" }],
};

export default function Dashboard({ scope = "member" }) {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const fullName = user?.profile?.full_name || user?.email;
  const roleLabel = t(`roles.${user?.role}`);
  const showAdminSummary = scope === "admin" && user?.permissions?.includes("reports.view");

  const cards = [
    { icon: Shield, title: t("dashboard.role"), value: roleLabel !== `roles.${user?.role}` ? roleLabel : user?.role },
    { icon: Flame, title: t("dashboard.status"), value: user?.account_status },
    { icon: Languages, title: t("common.language"), value: lang === "id" ? "🇮🇩 Bahasa Indonesia" : "🇬🇧 English" },
    { icon: UserRound, title: "Email", value: user?.email },
  ];

  return (
    <div data-testid={APP.dashboard} className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span data-testid="dashboard-scope" className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
            {t(`dashboard.scope_${scope}`)}
          </span>
          <h1 className="mt-2 font-[Outfit] text-4xl sm:text-5xl font-extrabold text-white tracking-tight">{fullName}</h1>
        </div>
        <Link to="/profile">
          <Button className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold h-11 px-5">
            {t("dashboard.viewProfile")}
          </Button>
        </Link>
      </div>

      {showAdminSummary && <AdminSummary />}

      {showAdminSummary && <StorageSavings />}

      {(scope === "coach" || (scope === "admin" && user?.permissions?.includes("member.view"))) && <MemberProgressPanel />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.title} className="bg-[#161923] border-white/5 hover:border-white/10 transition">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-widest text-slate-500 font-medium flex items-center gap-2">
                <c.icon className="h-3.5 w-3.5" /> {c.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold text-white truncate">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-gradient-to-br from-[#10121B] to-[#161923] border-white/5">
        <CardContent className="p-8 space-y-5">
          <p className="text-slate-400 max-w-2xl leading-relaxed text-sm">{t(`dashboard.hint_${scope}`)}</p>
          <div className="flex flex-wrap gap-3">
            {(SCOPE_LINKS[scope] || []).map((l) => (
              <Link key={l.to} to={l.to} data-testid={`dashboard-link-${l.to.replace(/\//g, "-")}`}>
                <Button variant="outline" className="h-10 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white">
                  {t(l.key)}
                </Button>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
